import { memo, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'

import { CONTROLS } from '@brickup/shared/locales/de/controls'
import {
  ACTIONS_COL_CLASS,
  DRAG_COL_CLASS,
  EXPAND_COL_CLASS,
  SELECTION_COL_CLASS,
  TREE_INDENT_PX,
  type DataTableColumn,
  type DropZone,
} from './types'
import {
  cn,
  Glyph,
  isElasticWidth,
  Menu,
  RowActions,
  TableCell,
  TableRow,
  type MenuAction,
} from '@/components/base'
import { SelectionCheckbox } from '@/components/Internal/Selection'
import { SortableItem, type DragMode } from '@/components/SortableItem'
import {
  readClickModifiers,
  type ClickModifiers,
} from '@/utils/click-modifiers'

type DataTableRowProps<T> = {
  item: T
  columns: DataTableColumn<T>[]
  /** The property each resized column reads its width from. One object for the
   *  whole table, and it changes only when a column gains or loses a width —
   *  dragging one wider writes the property, which no row has to be redrawn
   *  for. */
  columnVars?: Record<string, string>
  index: number
  isSelected: boolean
  onRowPress?: (item: T) => void | boolean
  /** The row's menu, built on render: passing the function rather than the
   *  finished array is what lets `memo` bail out, since a fresh array is never
   *  equal to the last one. */
  rowActions?: (item: T) => MenuAction[]

  /** Multi-select. `rowKey` and a stable `onSelectRow` are what let `memo`
   *  bail out on prop equality between renders. */
  selectable?: boolean
  rowKey?: string
  /** `indeterminate` where a branch holds some ticked leaves and some not. */
  isChecked?: boolean | 'indeterminate'
  /** The press, with any modifier held: the table decides range or single. */
  onSelectRow?: (key: string, modifiers: ClickModifiers) => void

  /** The tree's chevron column. `canExpand === false` leaves the spacer and no
   *  chevron, so labels stay aligned whether a row has children or not. */
  hasExpandColumn?: boolean
  isExpanded?: boolean
  canExpand?: boolean
  onToggleExpand?: (key: string) => void

  /** Nesting level. Indents the chevron column and everything after it; 0
   *  renders flush. */
  depth?: number
  /** Drawn as a band over the rows under it. See `TreeConfig.heading`. */
  isHeading?: boolean
  /** Whether the table drags at all. Off, the row is not in the context. */
  sortable?: boolean
  /**
   * Whether the table reserves a column for the grip. A row that cannot be
   * picked up still keeps it, or its cells sit a gutter left of the rest.
   */
  hasDragColumn?: boolean
  /** Whether THIS row can be picked up. A row that cannot still takes a drop. */
  draggable?: boolean
  dragMode?: DragMode
  /** Where a drag hovering this row would land. Undefined while none does. */
  dropZone?: DropZone
  /** The row's own indent, so an insertion line starts where its label does
   *  and reads at the depth the drop would land at. */
  dropIndent?: number
  /** A drag hovering elsewhere would land inside THIS row. Outlined so an
   *  insertion line one indent away cannot be read as the wrong branch. */
  isDropParent?: boolean
}

function DataTableRowInner<T>({
  item,
  columns,
  columnVars,
  index,
  isSelected,
  onRowPress,
  rowActions,
  selectable,
  rowKey,
  isChecked,
  onSelectRow,
  hasExpandColumn,
  isExpanded,
  canExpand,
  onToggleExpand,
  depth = 0,
  isHeading = false,
  sortable = false,
  hasDragColumn = false,
  draggable = false,
  dragMode,
  dropZone,
  dropIndent = 0,
  isDropParent = false,
}: DataTableRowProps<T>) {
  const expand = () => {
    if (rowKey !== undefined) onToggleExpand?.(rowKey)
  }
  const rowClickExpands = !!hasExpandColumn && !!canExpand

  const handleRowClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    // ctrl/cmd-click adds the row to the selection instead of opening it. The
    // plain click still opens, so the gesture stays unambiguous — which a
    // click that sometimes opens and sometimes selects would not be.
    const modifiers = readClickModifiers(event)
    if (modifiers.additive && onSelectRow && rowKey !== undefined) {
      onSelectRow(rowKey, modifiers)
      return
    }
    if (onRowPress) {
      // `false` means the handler declined; fall through to expansion.
      if (onRowPress(item) === false && rowClickExpands) expand()
      return
    }
    if (rowClickExpands) expand()
  }

  const isClickable = !!onRowPress || rowClickExpands
  const actions = rowActions?.(item)
  // Paid out of the chevron column, which leads the label it belongs to.
  // `depth` comes from `TreeConfig`, and a tree always has that column.
  const indent = depth > 0 ? depth * TREE_INDENT_PX : undefined

  // A band gives up one gutter, which is what sets its label left of the rows
  // under it: the fixed columns leading the row where it has any — a mark, a
  // thumbnail — and the drag handle's otherwise. Either way the measured
  // columns after it keep their edges. Asked only of a band; every other row
  // walks past it.
  const headingSkips = isHeading
    ? Math.max(
        columns.findIndex(column => isElasticWidth(column.width)),
        0,
      )
    : 0
  const shownColumns = headingSkips > 0 ? columns.slice(headingSkips) : columns

  const row = (dragHandle: ReactNode) => (
    <TableRow
      selected={isSelected}
      onClick={isClickable ? handleRowClick : undefined}
      // The menu appears under the pointer, so every row of a table that has
      // one says the pointer is on it — including a row whose own menu came
      // back empty, which still reserves the column.
      hovers={!!rowActions}
      heading={isHeading}
      className={isHeading ? 'py-1' : undefined}
    >
      {hasDragColumn && !(isHeading && headingSkips === 0) ? (
        <div className={DRAG_COL_CLASS}>{dragHandle}</div>
      ) : null}
      {selectable ? (
        <div className={SELECTION_COL_CLASS}>
          <SelectionCheckbox
            checked={isChecked ?? false}
            onToggle={modifiers => {
              if (rowKey !== undefined) onSelectRow?.(rowKey, modifiers)
            }}
          />
        </div>
      ) : null}

      {hasExpandColumn ? (
        <div className={EXPAND_COL_CLASS} style={{ paddingLeft: indent }}>
          {canExpand ? (
            <button
              type="button"
              onClick={event => {
                // Without this the row's own click fires too and the two
                // toggles cancel out.
                event.stopPropagation()
                expand()
              }}
              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-border"
            >
              {/* One caret that turns, like every other disclosure in the
                  app — two glyphs for one state read as two controls. */}
              <Glyph
                name="caret"
                size={13}
                className={cn(
                  'transition-transform',
                  isExpanded && 'rotate-90',
                )}
              />
            </button>
          ) : null}
        </div>
      ) : null}

      {shownColumns.map(column => (
        <TableCell
          key={column.key}
          width={column.width}
          widthVar={columnVars?.[column.key]}
          align={column.align}
        >
          {column.render?.(item, index)}
        </TableCell>
      ))}

      {rowActions ? (
        // Pinned flush with the scroller's right edge, so the menu stays
        // reachable however far a wide list has been scrolled sideways, and
        // `self-stretch` because a column holding only an absolutely
        // positioned child is nought high in a row that centres its cells.
        <div
          className={cn(ACTIONS_COL_CLASS, 'sticky right-0 z-10 self-stretch')}
        >
          {actions?.length ? (
            <RowActions variant="single">
              <Menu actions={actions} label={CONTROLS.dataTable.rowActions} />
            </RowActions>
          ) : null}
        </div>
      ) : null}
    </TableRow>
  )

  // A row that cannot be dragged still joins the context when the table is
  // sortable: it is where the dragged one lands.
  if (sortable && rowKey !== undefined) {
    return (
      <SortableItem id={rowKey} mode={dragMode} fixed={!draggable}>
        {({ handle, isOver }) => (
          <div className="relative">
            {row(handle)}
            {/* Over the row, not under it: the row paints a ground of its
                own, so a mark behind it is the one thing the reader does not
                see. The outline is what says the drop lands on this row
                rather than somewhere under the pointer. */}
            {dropZone === 'into' || (!dropZone && isOver) ? (
              <span
                aria-hidden
                className={cn(
                  DROP_OVERLAY,
                  'bg-drop',
                  dropZone === 'into' && 'rounded-md border-2 border-primary',
                )}
              />
            ) : null}
            {dropZone === 'before' || dropZone === 'after' ? (
              <DropLine zone={dropZone} indent={dropIndent} />
            ) : null}
            {/* Dashed and unfilled, against the solid outline of a drop that
                lands on the row itself: this one says "the drop goes in
                here", not "it goes on this". */}
            {isDropParent ? (
              <span
                aria-hidden
                className={cn(
                  DROP_OVERLAY,
                  'rounded-md border border-dashed border-primary',
                )}
              />
            ) : null}
          </div>
        )}
      </SortableItem>
    )
  }

  return row(null)
}

/** What every drop mark is laid over the row with. */
const DROP_OVERLAY = 'pointer-events-none absolute inset-0 z-10'

/**
 * Where a drop would insert the dragged row: a rule on the edge it lands at.
 *
 * Set in from the row's own indent so it starts under the label it will sit
 * beside — a line across the full width says nothing about the depth the row
 * would land at, which is half of what the reader is asking.
 *
 * @single-use the insertion marker of `DataTableRow`
 */
function DropLine({ zone, indent }: { zone: DropZone; indent: number }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute right-2 z-10 h-0.5 rounded-full bg-primary',
        zone === 'before' ? '-top-px' : '-bottom-px',
      )}
      style={{ left: 8 + indent * TREE_INDENT_PX }}
    />
  )
}

/**
 * @single-use part of `DataTable`, which is what a screen renders
 */
export const DataTableRow = memo(DataTableRowInner) as typeof DataTableRowInner
